import { db } from './firebase';
import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  getCountFromServer,
  query, 
  orderBy, 
  limit, 
  startAfter, 
  DocumentSnapshot,
  Timestamp,
  updateDoc,
  doc,
  increment
} from 'firebase/firestore';
import { LicensePlate, EmojiReaction } from '../types';

export class PlateService {
  private static readonly COLLECTION_NAME = 'plates';

  /**
   * Gets the total count of license plates in the collection
   */
  static async getPlateCount(): Promise<number> {
    try {
      const q = query(collection(db, this.COLLECTION_NAME));
      const snapshot = await getCountFromServer(q);
      return snapshot.data().count;
    } catch (error) {
      console.error('Error getting plate count:', error);
      return 0;
    }
  }

  /**
   * Gets a single plate by ID
   */
  static async getPlateById(plateId: string): Promise<LicensePlate | null> {
    try {
      const plateDoc = await getDoc(doc(db, this.COLLECTION_NAME, plateId));
      
      if (!plateDoc.exists()) {
        return null;
      }

      const data = plateDoc.data();
      return {
        id: plateDoc.id,
        ...data,
        uploadDate: data.uploadDate instanceof Timestamp 
          ? data.uploadDate 
          : new Date(data.uploadDate.seconds * 1000)
      } as LicensePlate;
    } catch (error) {
      console.error('Error getting plate by ID:', error);
      throw new Error('Failed to retrieve license plate');
    }
  }

  /**
   * Saves a new license plate to Firestore
   */
  static async savePlate(plateData: {
    imageUrl: string;
    state?: string;
    uploaderId?: string;
  }): Promise<string> {
    try {
      const plateDoc = {
        imageUrl: plateData.imageUrl,
        state: plateData.state || null,
        uploadDate: Timestamp.now(),
        uploaderId: plateData.uploaderId || null,
        votes: {
          upvotes: 0,
          downvotes: 0,
          total: 0
        },
        voteHistory: [],
        emojiReactions: []
      };

      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), plateDoc);
      return docRef.id;
    } catch (error) {
      console.error('Error saving plate:', error);
      throw new Error('Failed to save license plate');
    }
  }

  /**
   * Fetches all plates without pagination
   */
  static async getAllPlates(
    sortBy: 'newest' | 'oldest' | 'highest-rated' | 'lowest-rated' = 'newest'
  ): Promise<LicensePlate[]> {
    try {
      let q = query(collection(db, this.COLLECTION_NAME));

      // Add ordering
      switch (sortBy) {
        case 'newest':
          q = query(q, orderBy('uploadDate', 'desc'));
          break;
        case 'oldest':
          q = query(q, orderBy('uploadDate', 'asc'));
          break;
        case 'highest-rated':
          q = query(q, orderBy('votes.total', 'desc'));
          break;
        case 'lowest-rated':
          q = query(q, orderBy('votes.total', 'asc'));
          break;
      }

      const querySnapshot = await getDocs(q);
      const plates: LicensePlate[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        plates.push({
          id: doc.id,
          imageUrl: data.imageUrl,
          state: data.state,
          uploadDate: data.uploadDate,
          uploaderId: data.uploaderId,
          votes: data.votes,
          voteHistory: data.voteHistory || [],
          emojiReactions: data.emojiReactions || []
        });
      });

      return plates;
    } catch (error) {
      console.error('Error fetching all plates:', error);
      throw new Error('Failed to fetch all license plates');
    }
  }

  /**
   * Fetches plates with pagination
   */
  static async getPlates(
    sortBy: 'newest' | 'oldest' | 'highest-rated' | 'lowest-rated' = 'newest',
    pageSize: number = 10,
    lastDoc?: DocumentSnapshot
  ): Promise<{ plates: LicensePlate[]; lastDoc?: DocumentSnapshot }> {
    try {
      let q = query(collection(db, this.COLLECTION_NAME));

      // Add ordering
      switch (sortBy) {
        case 'newest':
          q = query(q, orderBy('uploadDate', 'desc'));
          break;
        case 'oldest':
          q = query(q, orderBy('uploadDate', 'asc'));
          break;
        case 'highest-rated':
          q = query(q, orderBy('votes.total', 'desc'));
          break;
        case 'lowest-rated':
          q = query(q, orderBy('votes.total', 'asc'));
          break;
      }

      // Add pagination
      q = query(q, limit(pageSize));
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const querySnapshot = await getDocs(q);
      const plates: LicensePlate[] = [];
      let newLastDoc: DocumentSnapshot | undefined;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        plates.push({
          id: doc.id,
          imageUrl: data.imageUrl,
          state: data.state,
          uploadDate: data.uploadDate,
          uploaderId: data.uploaderId,
          votes: data.votes,
          voteHistory: data.voteHistory || [],
          emojiReactions: data.emojiReactions || []
        });
        newLastDoc = doc;
      });

      return { plates, lastDoc: newLastDoc };
    } catch (error) {
      console.error('Error fetching plates:', error);
      throw new Error('Failed to fetch license plates');
    }
  }

  /**
   * Updates vote counts for a plate
   */
  static async updateVotes(
    plateId: string, 
    voteType: 'upvote' | 'downvote',
    voterId: string
  ): Promise<void> {
    try {
      const plateRef = doc(db, this.COLLECTION_NAME, plateId);
      
      // Note: This is a simplified approach. In production, you'd want to use a transaction
      // to check if user already voted and prevent duplicate voting

      const totalIncrement = voteType === 'upvote' ? 1 : -1;
      
      await updateDoc(plateRef, {
        [`votes.${voteType === 'upvote' ? 'upvotes' : 'downvotes'}`]: increment(1),
        'votes.total': increment(totalIncrement),
        voteHistory: increment(1) // This would need to be handled differently in production
      });
    } catch (error) {
      console.error('Error updating votes:', error);
      throw new Error('Failed to update votes');
    }
  }

  /**
   * Gets a single plate by ID
   */
  static async getPlate(plateId: string): Promise<LicensePlate | null> {
    try {
      const plateDoc = await getDocs(
        query(collection(db, this.COLLECTION_NAME), limit(1))
      );
      
      if (plateDoc.empty) {
        return null;
      }

      const doc = plateDoc.docs[0];
      const data = doc.data();
      
      return {
        id: doc.id,
        imageUrl: data.imageUrl,
        state: data.state,
        uploadDate: data.uploadDate,
        uploaderId: data.uploaderId,
        votes: data.votes,
        voteHistory: data.voteHistory || [],
        emojiReactions: data.emojiReactions || []
      };
    } catch (error) {
      console.error('Error fetching plate:', error);
      throw new Error('Failed to fetch license plate');
    }
  }

  /**
   * Gets plates by state
   */
  static async getPlatesByState(
    state: string,
    sortBy: 'newest' | 'oldest' | 'highest-rated' | 'lowest-rated' = 'newest',
    pageSize: number = 10
  ): Promise<LicensePlate[]> {
    try {
      let q = query(
        collection(db, this.COLLECTION_NAME),
        orderBy('uploadDate', sortBy === 'newest' ? 'desc' : 'asc'),
        limit(pageSize)
      );

      const querySnapshot = await getDocs(q);
      const plates: LicensePlate[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.state === state) {
          plates.push({
            id: doc.id,
            imageUrl: data.imageUrl,
            state: data.state,
            uploadDate: data.uploadDate,
            uploaderId: data.uploaderId,
            votes: data.votes,
            voteHistory: data.voteHistory || [],
            emojiReactions: data.emojiReactions || []
          });
        }
      });

      return plates;
    } catch (error) {
      console.error('Error fetching plates by state:', error);
      throw new Error('Failed to fetch plates by state');
    }
  }

  /**
   * Adds or updates an emoji reaction for a plate
   */
  static async addEmojiReaction(
    plateId: string,
    emoji: string,
    userId: string
  ): Promise<void> {
    try {
      const plateRef = doc(db, this.COLLECTION_NAME, plateId);
      const plateDoc = await getDoc(plateRef);
      
      if (!plateDoc.exists()) {
        throw new Error('Plate not found');
      }

      const data = plateDoc.data();
      const currentReactions: EmojiReaction[] = data.emojiReactions || [];
      
      // Get today's date string (YYYY-MM-DD)
      const today = new Date().toISOString().split('T')[0];
      
      // Find existing reaction for this emoji
      const existingReactionIndex = currentReactions.findIndex(
        reaction => reaction.emoji === emoji
      );

      let updatedReactions: EmojiReaction[];

      if (existingReactionIndex >= 0) {
        // Emoji already exists
        const existingReaction = currentReactions[existingReactionIndex];
        
        // Check if user already reacted today
        const userReactions = existingReaction.userReactions || {};
        if (userReactions[userId] === today) {
          // User already reacted today, do nothing
          return;
        }
        
        // Add the user and increment count
        updatedReactions = [...currentReactions];
        updatedReactions[existingReactionIndex] = {
          emoji,
          count: existingReaction.count + 1,
          users: [...existingReaction.users, userId],
          userReactions: {
            ...userReactions,
            [userId]: today
          }
        };
      } else {
        // New emoji reaction
        updatedReactions = [
          ...currentReactions,
          {
            emoji,
            count: 1,
            users: [userId],
            userReactions: {
              [userId]: today
            }
          }
        ];
      }

      await updateDoc(plateRef, {
        emojiReactions: updatedReactions
      });
    } catch (error) {
      console.error('Error adding emoji reaction:', error);
      throw new Error('Failed to add emoji reaction');
    }
  }
}
